// Funciones de planes de desplazamiento.
// index.js las pone en el state: se usan como this.$store.state.<función>(...). Son métodos, así que `this` es el state.
import { db } from "@/firebase";
import { query, collection, where, getDocs, limit, orderBy } from "firebase/firestore";
import localforage from "localforage";
import moment from "moment";
import _ from "lodash";
import { colecciones } from "../constantes";

export const funcionesPlanDesplazamiento = {
  // VERIFICAR NOVEDADES DEL PLAN DE DESPLAZAMIENTO TERRESTRE
  async verificarNovedadesPlanDesplazamiento(
    planDesplazamiento,
    modoSinConexion,
  ) {
    const { conductor, vehiculo, secciones } = planDesplazamiento;
    let novedades = [];

    if (!conductor.idLicencia)
      novedades.push("Licencia de conducción no registrada.");
    if (!conductor.idCapacitacion)
      novedades.push("Capacitación / curso vial no registrada.");
    if (!conductor.idAptitudFisica)
      novedades.push("Formulario de aptitud física no registrado.");
    if (!vehiculo.idSoat) novedades.push("SOAT no registrado.");
    if (!vehiculo.idTecnicoMecanica)
      novedades.push("Revisión técnico-mecánica no registrada.");
    if (!vehiculo.idInspeccion)
      novedades.push("Inspección pre-operacional no registrada.");

    try {
      const [
        licenciaConductor,
        capacitacionConductor,
        aptitudFisicaConductor,
        soatVehiculo,
        tecnicoMecanicaVehiculo,
        inspeccionVehiculo,
      ] = await Promise.all([
        this.obtenerLicenciaConductor(conductor.idLicencia, modoSinConexion),
        this.obtenerCapacitacionConductor(
          conductor.idCapacitacion,
          modoSinConexion,
        ),
        this.obtenerAptitudConductor(
          conductor.idAptitudFisica,
          modoSinConexion,
        ),
        this.obtenerDocumentoVehiculo(vehiculo.idSoat, modoSinConexion),
        this.obtenerDocumentoVehiculo(
          vehiculo.idTecnicoMecanica,
          modoSinConexion,
        ),
        this.obtenerInspeccion(vehiculo.idInspeccion, modoSinConexion),
      ]);
      const nuevasNovedades =
        this.obtenerNovedadesDocumentosPlanDesplazamiento(
          conductor,
          vehiculo,
          {
            capacitacionConductor,
            aptitudFisicaConductor,
            licenciaConductor,
            soatVehiculo,
            tecnicoMecanicaVehiculo,
            inspeccionVehiculo,
          },
        );
      novedades = novedades.concat(nuevasNovedades);
    } catch (error) {
      console.error("Error al verificar documentos:", error);
      return ["Error al verificar los documentos."];
    }

    // VALIDAR SECCIONES Y LOS CAMPOS CON RESPUESTA NO
    const seccionesConNovedad =
      this.obtenerNovedadesSeccionesPlanDesplazamiento(secciones);
    if (this.seccionesSinDiligenciarPlanDesplazamitno(secciones)) {
      novedades.push("No se ha completado el diligenciamiento del formato.");
    }
    novedades = novedades.concat(seccionesConNovedad);

    return novedades;
  },
  // OBTENER NOVEDADES DE LOS DOCUMENTOS DEL PLAN DE DESPLAZAMIENTO TERRESTRE
  obtenerNovedadesDocumentosPlanDesplazamiento(
    conductor,
    vehiculo,
    documentos,
  ) {
    const {
      licenciaConductor,
      capacitacionConductor,
      aptitudFisicaConductor,
      soatVehiculo,
      tecnicoMecanicaVehiculo,
      inspeccionVehiculo,
    } = documentos;
    const novedades = [];

    const estadoNoValido = (doc, estadosValidos) => {
      if (!doc) return true;

      if ("estado" in doc && estadosValidos.includes(doc.estado)) {
        return false;
      }

      if ("resultado" in doc && estadosValidos.includes(doc.resultado)) {
        return false;
      }

      return true;
    };

    // LICENCIA
    if (conductor.idLicencia && !licenciaConductor) {
      novedades.push("Licencia de conducción no encontrada en el sistema.");
    } else if (
      licenciaConductor &&
      Array.isArray(licenciaConductor.categorias)
    ) {
      const categoriasVencidas = licenciaConductor.categorias
        .filter((cat) => cat.esVigente === "No")
        .map((cat) => cat.id);
      if (categoriasVencidas.length > 0) {
        novedades.push(
          `Licencia de conducción con categorías vencidas: <b style='font-size: 13pt !important'>${categoriasVencidas.join(
            ", ",
          )}</b>.`,
        );
      }
    }

    // CAPACITACION
    if (conductor.idCapacitacion && !capacitacionConductor) {
      novedades.push(
        "Capacitación / curso vial no encontrada en el sistema.",
      );
    } else if (
      capacitacionConductor &&
      estadoNoValido(capacitacionConductor, ["Vigente", "Próximo a Vencer"])
    ) {
      novedades.push(
        "Capacitación / curso vial se encuentra <b style='font-size: 13pt !important'>Vencido</b>.",
      );
    }

    // APTITUD FISICA
    if (conductor.idAptitudFisica && !aptitudFisicaConductor) {
      novedades.push(
        "Formulario de aptitud física no encontrado en el sistema.",
      );
    } else if (
      aptitudFisicaConductor &&
      estadoNoValido(aptitudFisicaConductor, [
        "Apto",
        "Con Control Específico",
      ])
    ) {
      novedades.push(
        "Formulario de aptitud física se encuentra <b style='font-size: 13pt !important'>No Apto</b>.",
      );
    }

    // SOAT
    if (vehiculo.idSoat && !soatVehiculo) {
      novedades.push("SOAT no encontrado en el sistema.");
    } else if (
      soatVehiculo &&
      estadoNoValido(soatVehiculo, ["Vigente", "Próximo a Vencer"])
    ) {
      novedades.push(
        "SOAT se encuentra <b style='font-size: 13pt !important'>Vencido</b>.",
      );
    }

    // TÉCNICO MECÁNICA
    if (vehiculo.idTecnicoMecanica && !tecnicoMecanicaVehiculo) {
      novedades.push(
        "Revisión técnico-mecánica no encontrada en el sistema.",
      );
    } else if (
      tecnicoMecanicaVehiculo &&
      estadoNoValido(tecnicoMecanicaVehiculo, ["Vigente", "Próximo a Vencer"])
    ) {
      novedades.push(
        "Revisión técnico-mecánica se encuentra <b style='font-size: 13pt !important'>Vencido</b>.",
      );
    }

    // INSPECCIÓN DEL VEHÍCULO
    if (vehiculo.idInspeccion && !inspeccionVehiculo) {
      novedades.push(
        "Inspección pre-operacional no encontrada en el sistema.",
      );
    } else if (
      inspeccionVehiculo &&
      estadoNoValido(inspeccionVehiculo, ["Apto", "Con Control Específico"])
    ) {
      novedades.push(
        "Inspección pre-operacional se encuentra <b style='font-size: 13pt !important'>No Apto</b>.",
      );
    }

    return novedades;
  },
  // OBTENER NOVEDADES DE LAS SECCIONES (FORMATO) DEL PLAN DE DESPLAZAMIENTO TERRESTRE
  obtenerNovedadesSeccionesPlanDesplazamiento(secciones) {
    const seccionesValidas = [
      { id: 0, campos: [2] },
      { id: 1, campos: [1, 2, 3] },
      { id: 2, campos: [2, 3, 4, 5, 6] },
      { id: 3, campos: [1, 2] },
    ];

    const novedades = [];

    secciones.forEach((seccion) => {
      const seccionConfig = seccionesValidas.find((s) => s.id === seccion.id);

      if (!seccionConfig) return;

      seccion.campos.forEach((campo) => {
        if (
          seccionConfig.campos.includes(campo.id) &&
          campo.respuesta === "No"
        ) {
          novedades.push(`
              Sección <b style='font-size: 13pt !important'>${seccion.nombre}</b>: El campo <b style='font-size: 13pt !important'>${campo.nombre}</b> tiene una respuesta negativa 
              (<b style='font-size: 13pt !important'>No</b>).
            `);
        }
      });
    });

    return novedades;
  },
  // SECCIONES QUE NO HAN SIDO DILIGENCIADAS
  seccionesSinDiligenciarPlanDesplazamitno(secciones) {
    let hayCampoConRespuesta = false;

    secciones.forEach((seccion) => {
      seccion.campos.forEach((campo) => {
        if (
          (campo.tipo === "Opciones" && campo.respuesta === null) ||
          (campo.tipo === "Múltiple" && campo.respuesta.length === 0)
        ) {
          hayCampoConRespuesta = true;
        }
      });
    });

    return hayCampoConRespuesta;
  },
  // OBTENER ULTIMO PLAN DESPLAZAMIENTO DEL USUARIO
  async obtenerUltimaPlanDesplazamiento(
    idEmpresa,
    idUsuario,
    fecha,
    modoSinConexion,
  ) {
    if (modoSinConexion) {
      const registros = await localforage.getItem(
        colecciones.localforage.seguridadVial.planesDesplazamientos.registros,
      );
      const planesDesplazamientos = registros
        ? registros
          .filter(
            (item) =>
              item.idEmpresa === idEmpresa &&
              item.registro.idUsuario === idUsuario &&
              item.registro.filtro === fecha,
          )
          .map((item) => {
            const nuevoItem = { ...item };
            nuevoItem.registro.creacion = moment(
              new Date(
                `${item.registro.realizacion.fecha} ${item.registro.realizacion.hora}`,
              ),
            ).format("YYYY-MM-DD HH:mm:ss");
            return {
              ...nuevoItem,
            };
          })
        : [];
      const filtro = _.orderBy(
        planesDesplazamientos,
        ["registro.creacion"],
        ["desc"],
      );
      return filtro.length > 0 ? filtro[0] : null;
    } else {
      const q = query(
        collection(
          db,
          colecciones.firebase.seguridadVial.planesDesplazamientos.registros,
        ),
        where("idEmpresa", "==", idEmpresa),
        where("registro.idUsuario", "==", idUsuario),
        where("registro.filtro", "==", fecha),
        where("registro.estado", "in", ["Creado", "Editado"]),
        orderBy("registro.creacion", "desc"),
        limit(1),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.length > 0
        ? querySnapshot.docs[0].data()
        : null;
    }
  },
  // OBTENER DESCANSOS DEL PLAN DE DESPLAZAMIENTOS
  async obtenerDescansosPlanDesplazamiento(
    idPlanDesplazamiento,
    modoSinConexion,
  ) {
    // eslint-disable-next-line no-empty -- sin conexión no consulta nada
    if (modoSinConexion) {
    } else {
      const q = query(
        collection(
          db,
          colecciones.firebase.seguridadVial.planesDesplazamientos.reportes
            .registros,
        ),
        where("idPlanDesplazamiento", "==", idPlanDesplazamiento),
        where("estado", "==", "En Descanso"),
        where("registro.estado", "in", ["Creado", "Editado"]),
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.length > 0
        ? querySnapshot.docs.map((doc) => doc.data())
        : null;
    }
  },
};
